import { inject, Injectable } from "@angular/core";
import { DateTimeUnit } from "luxon";
import { map, Observable } from "rxjs";
import { TelemetryResponse } from "../../../models/shared/telemetry";
import { DoughnutChartData } from "../../../models/ui/chartData";
import { EnergyLossData, BarChartData, LineChartData } from "../../../models/ui/energyToolCharts";
import { EnergyToolExtractionService } from "../energy-tool-extraction/energy-tool-extraction.service";
import { TelemetryService } from "../../api/telemetry/telemetry.service";
import { EnergyToolUtilsService } from "../../utils/energy-tool-utils/energy-tool-utils.service";

@Injectable({
  providedIn: "root",
})
export class EnergyToolUpdateService {
  private energyExtraction = inject(EnergyToolExtractionService);
  private energyToolUtils = inject(EnergyToolUtilsService);
  private telemetryService = inject(TelemetryService);

  /**
   * Update total energy loss widget with new data
   * @param assetId id of the asset
   * @param timeWindow time window for the data
   * @param energyLossStateSetter setter for the energy loss value
   * @param energyCostStateSetter setter for the energy cost value
   * @param sourceObservable observable that returns the data
   */
  public updateEnergyLossForTimeWindow(
    assetId: string,
    timeWindow: string,
    energyLossStateSetter: (value: number) => void,
    energyCostStateSetter: (value: number) => void,
    sourceObservable: (assetId: string, start: number, end: number) => Observable<TelemetryResponse | null>
  ) {
    const { start, end } = this.energyToolUtils.getStartAndEnd(timeWindow);
    sourceObservable(assetId, start, end).subscribe({
      next: (res) => {
        this.energyExtraction.extractAndSetTotalEnergyLoss(res, timeWindow, energyLossStateSetter);
        this.energyExtraction.extractAndSetEnergyCost(res, timeWindow, energyCostStateSetter);
      },
    });
  }

  /**
   * Update doughnut chart with new data
   * @param assetId id of the asset
   * @param timeWindow time window for the data
   * @param doughnutChartStateSetter setter for the doughnut chart data
   * @param sourceObservable observable that returns the data
   */
  public updateDoughnutChartForTimeWindow(
    assetId: string,
    timeWindow: string,
    doughnutChartStateSetter: (value: DoughnutChartData[]) => void,
    sourceObservable: (assetId: string, start: number, end: number) => Observable<EnergyLossData[] | null>
  ) {
    const { start, end } = this.energyToolUtils.getStartAndEnd(timeWindow);
    sourceObservable(assetId, start, end).subscribe({
      next: (res) => {
        if (res) {
          this.energyExtraction.extractAndSetDoughnutChart(res, doughnutChartStateSetter);
        }
      },
    });
  }

  /**
   * Update bar chart with new data
   * @param assetId id of the asset
   * @param assetLabel label of the asset
   * @param start start time of the data
   * @param end end time of the data
   * @param timeFrame time frame for the data
   * @param singleSource boolean that indicates if the data is from a single source
   * @param withAverage boolean that indicates if the data should include average
   * @param stateSignal setter for the bar chart data
   * @param sourceObservable observable that returns the data
   */
  public updateBarChartForTimeWindow(
    assetId: string,
    assetLabel: string | undefined,
    start: number,
    end: number,
    timeFrame: DateTimeUnit,
    singleSource: boolean,
    withAverage: boolean,
    stateSignal: (value: BarChartData[]) => void,
    sourceObservable: (
      assetId: string,
      start: number,
      end: number
    ) => Observable<TelemetryResponse | EnergyLossData[] | null>
  ) {
    sourceObservable(assetId, start, end)
      .pipe(
        map((res) => {
          let data: TelemetryResponse | EnergyLossData[];
          if (singleSource) {
            data = res as TelemetryResponse;
            return [
              {
                source: { label: assetLabel, id: assetId },
                energy_loss: data.energy_loss,
              },
            ];
          } else {
            data = res as EnergyLossData[];
            return data;
          }
        })
      )
      .subscribe({
        next: (res) => {
          this.energyExtraction.extractAndSetBarChartData(res, withAverage, start, timeFrame, stateSignal);
        },
      });
  }

  /**
   * Update line chart with new data
   * @param assetId id of the asset
   * @param start start time of the data
   * @param end end time of the data
   * @param timeFrame time frame for the data
   * @param lineChartStateSetter setter for the line chart data
   * @param sourceObservable observable that returns the data
   */
  public updateLineChartForTimeWindow(
    assetId: string,
    start: number,
    end: number,
    timeFrame: DateTimeUnit,
    lineChartStateSetter: (value: LineChartData[]) => void,
    sourceObservable: (assetId: string, start: number, end: number) => Observable<TelemetryResponse | null>
  ) {
    sourceObservable(assetId, start, end).subscribe({
      next: (res) => {
        if (res) {
          this.energyExtraction.extractAndSetLineChartData(
            res.energy_loss,
            res.normal_energy_loss,
            start,
            timeFrame,
            lineChartStateSetter
          );
        }
      },
    });
  }

  /**
   * Update cycle with new data
   * @param assetId id of the asset
   * @param assetType type of the asset
   * @param timeWindow time window for the data
   * @param cycleStateSetter setter for the cycle value
   */
  public updateCycleForTimeWindow(
    assetId: string,
    assetType: string,
    timeWindow: string,
    cycleStateSetter: (value: { value: number; timestamp: string }) => void
  ) {
    let sourceObservable: Observable<TelemetryResponse | null>;
    if (timeWindow === "total") {
      sourceObservable = this.telemetryService.getTimeseries(assetId, assetType, "cycle");
    } else {
      const { start, end } = this.energyToolUtils.getStartAndEnd(timeWindow);
      sourceObservable = this.telemetryService.getTimeseries(assetId, assetType, "cycle", start, end, "DESC");
    }

    sourceObservable.subscribe({
      next: (res) => {
        if (res) {
          this.energyExtraction.extractAndSetCycle(res, timeWindow, cycleStateSetter);
        }
      },
    });
  }
}
